(() => {
  class EcomSavingsCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.form = this.querySelector('[data-ecom-savings-form]');
      this.bottlesInput = this.querySelector('[data-ecom-savings-bottles]');
      this.bottlePriceInput = this.querySelector('[data-ecom-savings-bottle-price]');
      this.productInput = this.querySelector('[data-ecom-savings-product]');
      this.durationInput = this.querySelector('[data-ecom-savings-duration]');
      this.bottlesOutput = this.querySelector('[data-ecom-savings-bottles-avoided]');
      this.currentOutput = this.querySelector('[data-ecom-savings-current]');
      this.hydrosOutput = this.querySelector('[data-ecom-savings-hydros]');
      this.differenceOutput = this.querySelector('[data-ecom-savings-difference]');
      this.differenceLabel = this.querySelector('[data-ecom-savings-difference-label]');
      this.differenceRow = this.querySelector('[data-ecom-savings-difference-row]');

      this.money = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
        minimumFractionDigits: 2,
      });

      this.form?.addEventListener('submit', (event) => event.preventDefault());
      this.form?.addEventListener('input', () => this.update());
      this.form?.addEventListener('change', () => this.update());
      this.update();
    }

    numberValue(input, fallback = 0) {
      const value = Number.parseFloat(input?.value ?? '');
      return Number.isFinite(value) ? Math.max(0, value) : fallback;
    }

    update() {
      const bottlesPerWeek = this.numberValue(this.bottlesInput);
      const bottlePrice = this.numberValue(this.bottlePriceInput);
      const months = Math.max(1, this.numberValue(this.durationInput, 12));
      const bottleCount = Math.round(bottlesPerWeek * 52 * (months / 12));
      const currentCost = Math.round(bottleCount * bottlePrice * 100);
      const productKey = this.productInput?.value === 'lumos' ? 'lumos' : 'hynox';
      const productPrice = this.numberValue(
        { value: this.dataset[productKey === 'lumos' ? 'lumosPrice' : 'hynoxPrice'] },
      );
      const filterPrice = this.numberValue({ value: this.dataset.filterPrice });
      const replacementFilters = productKey === 'hynox' ? Math.max(0, Math.ceil(months / 2) - 1) : 0;
      const hydrosCost = Math.round(productPrice + replacementFilters * filterPrice);
      const difference = currentCost - hydrosCost;

      if (this.bottlesOutput) this.bottlesOutput.textContent = bottleCount.toLocaleString('fr-FR');
      if (this.currentOutput) this.currentOutput.textContent = this.money.format(currentCost / 100);
      if (this.hydrosOutput) this.hydrosOutput.textContent = this.money.format(hydrosCost / 100);
      if (this.differenceOutput) this.differenceOutput.textContent = this.money.format(Math.abs(difference) / 100);
      if (this.differenceLabel) {
        this.differenceLabel.textContent = difference >= 0 ? 'Économie potentielle' : 'Budget supplémentaire';
      }
      this.differenceRow?.classList.toggle('is-negative', difference < 0);
    }
  }

  if (!customElements.get('ecom-savings-calculator')) {
    customElements.define('ecom-savings-calculator', EcomSavingsCalculator);
  }

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  ready(() => {
    const root = document.documentElement;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    document.querySelectorAll('[data-ecom-campaign]').forEach((campaign) => {
      const messages = [...campaign.querySelectorAll('[data-ecom-campaign-message]')];
      const control = campaign.querySelector('[data-ecom-campaign-control]');
      const messageRegion = campaign.querySelector('.ecom-campaign__messages');
      let index = Math.max(0, messages.findIndex((message) => message.classList.contains('is-active')));
      let timer;
      let paused = false;
      let hovered = false;
      let focusWithin = false;

      const render = () => {
        messages.forEach((message, messageIndex) => {
          const active = messageIndex === index;
          message.classList.toggle('is-active', active);
          message.setAttribute('aria-hidden', active ? 'false' : 'true');
          message.inert = !active;
        });
      };

      const stop = () => window.clearTimeout(timer);
      const schedule = () => {
        stop();
        const shouldRotate = !paused && !motionQuery.matches && messages.length > 1 && !document.hidden && !hovered && !focusWithin;
        messageRegion?.setAttribute('aria-live', shouldRotate ? 'off' : 'polite');
        if (!shouldRotate) return;
        timer = window.setTimeout(() => {
          index = (index + 1) % messages.length;
          render();
          schedule();
        }, 5200);
      };

      control?.addEventListener('click', () => {
        paused = !paused;
        campaign.classList.toggle('is-paused', paused);
        control.setAttribute('aria-label', paused ? 'Relancer les annonces' : 'Mettre les annonces en pause');
        if (!paused) focusWithin = false;
        schedule();
      });

      campaign.addEventListener('mouseenter', () => {
        hovered = true;
        schedule();
      });
      campaign.addEventListener('mouseleave', () => {
        hovered = false;
        schedule();
      });
      campaign.addEventListener('focusin', () => {
        focusWithin = true;
        schedule();
      });
      campaign.addEventListener('focusout', (event) => {
        focusWithin = campaign.contains(event.relatedTarget);
        schedule();
      });
      document.addEventListener('visibilitychange', schedule);
      motionQuery.addEventListener('change', schedule);
      render();
      schedule();
    });

    const closeMegaMenus = (except = null) => {
      document.querySelectorAll('.hydros-header__mega[open]').forEach((details) => {
        if (details !== except) {
          const summary = details.querySelector('summary');
          const restoreFocus = details.contains(document.activeElement);
          details.removeAttribute('open');
          summary?.setAttribute('aria-expanded', 'false');
          if (restoreFocus) summary?.focus();
        }
      });
    };

    document.querySelectorAll('.hydros-header__mega').forEach((details) => {
      details.addEventListener('toggle', () => {
        details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
        if (details.open) closeMegaMenus(details);
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.hydros-header__mega')) closeMegaMenus();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeMegaMenus();
    });

    const whatsapp = document.querySelector('[data-ecom-whatsapp]');
    if (!whatsapp) return;

    const stickySelectors = [
      '[data-hynox-sticky]',
      '[data-lumos-sticky]',
      '.hynox-sticky',
      '.lumos-sticky',
    ];

    const updateWhatsappOffset = () => {
      const fallback = 'calc(18px + env(safe-area-inset-bottom))';
      const visibleSticky = stickySelectors
        .flatMap((selector) => [...document.querySelectorAll(selector)])
        .filter((element) => {
          const style = window.getComputedStyle(element);
          const rect = element.getBoundingClientRect();
          return (
            style.position === 'fixed' &&
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            Number.parseFloat(style.opacity || '1') > 0.05 &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > window.innerHeight - 4
          );
        })
        .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];

      if (!visibleSticky) {
        root.style.setProperty('--ecom-whatsapp-bottom', fallback);
        return;
      }

      const rect = visibleSticky.getBoundingClientRect();
      const offset = Math.ceil(window.innerHeight - rect.top + 14);
      root.style.setProperty('--ecom-whatsapp-bottom', `calc(${offset}px + env(safe-area-inset-bottom))`);
    };

    let ticking = false;
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        updateWhatsappOffset();
      });
    };

    updateWhatsappOffset();
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('scroll', requestUpdate, { passive: true });

    if (typeof MutationObserver === 'function') {
      const observer = new MutationObserver(requestUpdate);
      stickySelectors.flatMap((selector) => [...document.querySelectorAll(selector)]).forEach((element) => {
        observer.observe(element, { attributes: true, attributeFilter: ['class', 'style'] });
      });
    }
  });
})();
